import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

import { Popover, PopoverModule } from 'primeng/popover';
import { DatePickerModule } from 'primeng/datepicker';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { addDays, subDays, startOfWeek, endOfWeek, subWeeks, startOfMonth, endOfMonth, subMonths, isSameDay, startOfDay, endOfDay } from 'date-fns';

import { LanguageService } from '@shared/services/language.service';
import { DatepickerTimeRangeTips } from '@src/app/enum/datepicker.enum';

type Range = [Date, Date];

@Component({
  selector: 'app-date-picker',
  standalone: true,
  imports: [CommonModule, PopoverModule, TranslateModule, DatePickerModule, FormsModule, ButtonModule],
  templateUrl: './date-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./date-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true,
    },
  ],
})
export class DatePickerComponent implements ControlValueAccessor, OnInit {

  private readonly TIME_RANGE_TIPS_CONFIG = [
    { label: 'labels.today', type: DatepickerTimeRangeTips.Today, value: this.today },
    { label: 'labels.yesterday', type: DatepickerTimeRangeTips.Yesterday, value: this.yesterday },
    { label: 'labels.beforeYesterday', type: DatepickerTimeRangeTips.DayBeforeYesterday, value: this.dayBeforeYesterday },
    { label: 'labels.lastWeek', type: DatepickerTimeRangeTips.PreviousWeek, value: this.previousWeek },
    { label: 'labels.lastMonth', type: DatepickerTimeRangeTips.PreviousMonth, value: this.previousMonth },
  ];

  @Input() public selectionMode: 'single' | 'multiple' | 'range' = 'single';
  @Input() public maxRangeDays: number;
  @Input() public maxSelectableDays: number;
  @Input() public label: string = this.translateService.instant('date');
  @Input() public numberOfMonths: number = 1;
  @Input() public isVisibleTimePicker = false;

  @Input('timeRangeTipsData') public set timeRangeTipsData(data: DatepickerTimeRangeTips[]) {
    if (data) {
      this.timeRangeTips = this.TIME_RANGE_TIPS_CONFIG.filter(item => data.includes(item.type));
    }
  }

  public _value: any; // Date | Date[]
  public initialViewDate: Date;
  public timeRangeTips = [...this.TIME_RANGE_TIPS_CONFIG];

  public readonly langauge: string = this.languageService.current;
  @Input() public maxDate: Date | null = new Date();

  public timePickerFrom: Date | undefined;
  public timePickerTo: Date | undefined;
  public showTimePicker = false;
  public showSelectDateWarning = false;

  constructor(private languageService: LanguageService, private translateService: TranslateService) {}

  ngOnInit(): void {
    this.initialViewDate = new Date();
  }

  writeValue(value: Date): void {
    this._value = value;
  }

  registerOnChange(fn: (value: Date) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public resetValue(op: Popover): void {
    this.showTimePicker = false;
    this.maxDate = new Date();
    this.onDateChange(null, op);
    op.hide();
  }

  onDateChange(value: any, op: Popover): void {
    if (this.selectionMode === 'range' && !this.showTimePicker) {
      const [start, end] = value || [];
      if (start && end) {
        value = [startOfDay(start), endOfDay(end)] as Range;
      }
    }

    this._value = value;
    this.onChange(value);
    this.onTouched();

    if (this.showSelectDateWarning) {
      this.toggleTimePicker();
    }

    if (this.selectionMode === 'range' && this.maxRangeDays) {
      const [start, end] = value || [];

      if (start && !end) {
        this.maxDate = addDays(start, this.maxRangeDays);
      }
      else {
        this.maxDate = new Date();
      }
    } else {
      this.maxDate = new Date();
    }

    if (this.selectionMode === 'range') {
      const [start, end] = value || [];
      if (start && end && !this.showTimePicker && !this.showSelectDateWarning) op.hide();
      return;
    }

    if (!this.showTimePicker && !this.showSelectDateWarning) {
      op.hide();
    }
  }

  private onChange: (value: Date) => void = () => {};
  private onTouched: () => void = () => {};

  public setInitialViewDate(): void {
    let baseDate: Date;

    if (this.selectionMode === 'range' && Array.isArray(this._value) && this._value.length > 0) {
      let [start, end] = this._value;

      if (start && !end) {
        end = endOfDay(start);
        this._value = [start, end];
        this.onChange(this._value);
      }

      const firstDate = start;
      const secondDate = end;

      const isRangeSpanningTwoMonths = firstDate && secondDate &&
        (firstDate.getMonth() !== secondDate.getMonth() ||
        firstDate.getFullYear() !== secondDate.getFullYear());

      if (this.numberOfMonths === 2 && isRangeSpanningTwoMonths) {
        baseDate = firstDate;
      }
      else if (this.numberOfMonths === 2) {
        baseDate = subMonths(firstDate, 1);
      }
      else {
        baseDate = firstDate;
      }
    }
    else if (this._value instanceof Date) {
      baseDate = this._value;
    }
    else {
      baseDate = new Date();
    }

    this.initialViewDate = baseDate;
  }

  public applyPreset(range: Range, op: Popover): void {
    if (this.selectionMode !== 'range') {
      this.onDateChange(range[0], op);
    }
    else {
      this.onDateChange(range, op);
    }

    if (!this.showTimePicker && !this.showSelectDateWarning) op.hide();
  }

  public get today(): Range {
    const d = new Date();
    return [d, d];
  }

  public get yesterday(): Range {
    const d = subDays(new Date(), 1);
    return [d, d];
  }

  public get dayBeforeYesterday(): Range {
    const d = subDays(new Date(), 2);
    return [d, d];
  }

  public get previousWeek(): Range {
    const lastWeekBase = subWeeks(new Date(), 1);
    const from = startOfWeek(lastWeekBase, { weekStartsOn: 1 });
    const to = endOfWeek(lastWeekBase, { weekStartsOn: 1 });
    return [from, to];
  }

  public get previousMonth(): Range {
    const lastMonthBase = subMonths(new Date(), 1);
    const from = startOfMonth(lastMonthBase);
    const to = endOfMonth(lastMonthBase);
    return [from, to];
  }

  public isPresetActive(range: Range): boolean {
    if (this.selectionMode !== 'range' || !this._value || this._value.length < 2) {
      return false;
    }
    const [start, end] = this._value as Range;
    return isSameDay(start, range[0]) && isSameDay(end, range[1]);
  }

  public toggleTimePicker(): void {
    if (!this._value || (this.selectionMode === 'range' && (!this._value[0] || !this._value[1]))) {
      this.showSelectDateWarning = true;
      return;
    }

    this.showSelectDateWarning = false;

    if (this.selectionMode === 'single') {
      this.timePickerFrom = startOfDay(new Date(this._value));
    }

    if (this.selectionMode === 'range') {
      const [start, end] = this._value;
      this.timePickerFrom = startOfDay(new Date(start));
      if (end) this.timePickerTo = endOfDay(new Date(end));
    }

    this.showTimePicker = !this.showTimePicker;
  }

  public applyTime(op: Popover): void {
    if (!this._value) return;

    if (this.selectionMode === 'single' && this.timePickerFrom) {
      const updated = new Date(this._value);
      updated.setHours(this.timePickerFrom.getHours(), this.timePickerFrom.getMinutes());
      this._value = updated;
      this.onChange(updated);
    }

    if (this.selectionMode === 'range') {
      let [start, end] = this._value;

      if (start && this.timePickerFrom) {
        start = new Date(start);
        start.setHours(this.timePickerFrom.getHours(), this.timePickerFrom.getMinutes());
      }

      if (end && this.timePickerTo) {
        end = new Date(end);
        end.setHours(this.timePickerTo.getHours(), this.timePickerTo.getMinutes());
      }

      this._value = [start, end];
      this.onChange(this._value);
    }

    op.hide();
  }
}
